import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  Clock,
  Headphones,
  Library,
  Target,
  X,
  Zap
} from 'lucide-react';
import { PersianText } from './shared/PersianText';
import { useAppLocale } from '../providers/LocaleProvider';
import { pickLocalized, pickLocalizedTitle } from '../utils/localizedContent';
import { localeMirroredNavArrowClass } from '../utils/localeNavArrow';
import { normalizeAppHostedUploadUrl } from '../utils/normalizeAppHostedUploadUrl';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';

export type HomeDemoPreviewKind = 'lesson' | 'quiz' | 'exercise' | 'book';

export interface HomeDemoPreviewLesson {
  id: number;
  title: string;
  titleFa?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  thumbnailUrl?: string | null;
  xpScore: number;
  courseTitle: string;
  courseTitleFa?: string | null;
  levelTitle: string;
  levelTitleFa?: string | null;
  sampleExcerpt?: string | null;
  sampleExcerptFa?: string | null;
  sampleBlockTitle?: string | null;
  sampleBlockTitleFa?: string | null;
  sampleHasAudio?: boolean;
}

export interface HomeDemoPreviewPractice {
  id: number;
  kind: string;
  title: string;
  titleFa?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  xpScore: number;
  questionCount: number;
  timeLimitMinutes?: number | null;
  passingScore?: number | null;
  lessonTitle: string;
  lessonTitleFa?: string | null;
}

export interface HomeDemoPreviewBook {
  id: number;
  title: string;
  farsiTitle?: string | null;
  author?: string | null;
  farsiAuthor?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  coverImageUrl?: string | null;
  difficulty: string;
  bookType: string;
  sampleExcerpt?: string | null;
  sampleBlockTitle?: string | null;
  sampleHasAudio?: boolean;
}

export interface HomeProductDemoPreviewProps {
  open: boolean;
  kind: HomeDemoPreviewKind;
  onClose: () => void;
  lesson?: HomeDemoPreviewLesson | null;
  practice?: HomeDemoPreviewPractice | null;
  book?: HomeDemoPreviewBook | null;
}

type GuideTarget =
  | 'lesson-header'
  | 'lesson-content'
  | 'lesson-practice'
  | 'quiz-header'
  | 'quiz-timer'
  | 'quiz-question'
  | 'quiz-submit'
  | 'exercise-header'
  | 'exercise-prompt'
  | 'exercise-check'
  | 'exercise-progress'
  | 'book-cover'
  | 'book-meta'
  | 'book-pages'
  | 'book-review';

const GUIDE_BY_KIND: Record<HomeDemoPreviewKind, GuideTarget[]> = {
  lesson: ['lesson-header', 'lesson-content', 'lesson-practice'],
  quiz: ['quiz-header', 'quiz-timer', 'quiz-question', 'quiz-submit'],
  exercise: ['exercise-header', 'exercise-prompt', 'exercise-check', 'exercise-progress'],
  book: ['book-cover', 'book-meta', 'book-pages', 'book-review']
};

function GuideSpotlight({
  active,
  onSelect,
  children,
  className = ''
}: {
  active: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`relative w-full rounded-xl text-start transition-[box-shadow,transform,opacity] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
        active
          ? 'z-20 scale-[1.01] shadow-[0_0_0_3px_rgba(217,119,6,0.85),0_12px_28px_rgba(0,0,0,0.18)] dark:shadow-[0_0_0_3px_rgba(251,191,36,0.75),0_12px_28px_rgba(0,0,0,0.45)]'
          : 'opacity-70 hover:opacity-95'
      } ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Mock product preview with a short object-by-object guide.
 * Uses real sample titles/media; layout mirrors learner UI without requiring auth.
 */
export const HomeProductDemoPreview: React.FC<HomeProductDemoPreviewProps> = ({
  open,
  kind,
  onClose,
  lesson,
  practice,
  book
}) => {
  const { t } = useTranslation('home');
  const { locale, direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [visible, setVisible] = useState(false);
  const [guideIndex, setGuideIndex] = useState(0);
  const targets = GUIDE_BY_KIND[kind];
  const activeTarget = targets[guideIndex] ?? targets[0]!;
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return undefined;
    }
    setGuideIndex(0);
    if (prefersReducedMotion()) {
      setVisible(true);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(id);
  }, [open, kind]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setVisible(false);
      window.setTimeout(onClose, prefersReducedMotion() ? 0 : 220);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = stageRef.current?.querySelector<HTMLElement>(`[data-demo-guide="${activeTarget}"]`);
    el?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
  }, [activeTarget, open, kind]);

  if (!open) return null;

  const isLastGuide = guideIndex >= targets.length - 1;

  const close = () => {
    setVisible(false);
    window.setTimeout(onClose, prefersReducedMotion() ? 0 : 220);
  };

  const goNextGuide = () => {
    if (isLastGuide) {
      close();
      return;
    }
    setGuideIndex((i) => Math.min(i + 1, targets.length - 1));
  };

  const goBackGuide = () => {
    if (guideIndex > 0) setGuideIndex((i) => i - 1);
  };

  const selectTarget = (target: GuideTarget) => {
    const idx = targets.indexOf(target);
    if (idx >= 0) setGuideIndex(idx);
  };

  const lessonTitle = lesson ? pickLocalizedTitle(lesson, locale) : '';
  const courseTitle = lesson
    ? pickLocalized(lesson.courseTitle, lesson.courseTitleFa, locale)
    : '';
  const levelTitle = lesson
    ? pickLocalized(lesson.levelTitle, lesson.levelTitleFa, locale)
    : '';
  const lessonDesc = lesson ? pickLocalized(lesson.description, lesson.descriptionFa, locale) : '';
  const lessonExcerpt = lesson
    ? pickLocalized(lesson.sampleExcerpt, lesson.sampleExcerptFa, locale)
    : '';
  const lessonBlockTitle = lesson
    ? pickLocalized(lesson.sampleBlockTitle, lesson.sampleBlockTitleFa, locale)
    : '';
  const lessonThumb = lesson?.thumbnailUrl ? normalizeAppHostedUploadUrl(lesson.thumbnailUrl) : null;

  const practiceTitle = practice ? pickLocalizedTitle(practice, locale) : '';
  const practiceLesson = practice
    ? pickLocalized(practice.lessonTitle, practice.lessonTitleFa, locale)
    : '';

  const bookTitle = book ? pickLocalized(book.title, book.farsiTitle, locale) : '';
  const bookAuthor = book ? pickLocalized(book.author, book.farsiAuthor, locale) : '';
  const bookDesc = book ? pickLocalized(book.description, book.descriptionFa, locale) : '';
  const bookExcerpt = book?.sampleExcerpt?.trim() || '';
  const bookBlockTitle = book?.sampleBlockTitle?.trim() || '';
  const bookCover = book?.coverImageUrl ? normalizeAppHostedUploadUrl(book.coverImageUrl) : null;

  const renderLessonStage = () => (
    <div className="space-y-3">
      <div data-demo-guide="lesson-header">
        <GuideSpotlight
          active={activeTarget === 'lesson-header'}
          onSelect={() => selectTarget('lesson-header')}
        >
          <div className="overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-white dark:border-amber-600/45 dark:from-amber-900/45 dark:to-slate-800">
            <div className="flex gap-3 p-3">
              <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg bg-amber-100 dark:bg-amber-900/50">
                {lessonThumb ? (
                  <img src={lessonThumb} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-amber-700 dark:text-amber-300">
                    <BookOpen className="h-6 w-6" aria-hidden />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800/80 dark:text-amber-300">
                  {t('demo.preview.lessonBadge')}
                </p>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  <PersianText>{lessonTitle}</PersianText>
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300">
                  {t('demo.fromPath', { course: courseTitle, level: levelTitle })}
                </p>
              </div>
            </div>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="lesson-content">
        <GuideSpotlight
          active={activeTarget === 'lesson-content'}
          onSelect={() => selectTarget('lesson-content')}
        >
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/70">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-100">
              <Headphones className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" aria-hidden />
              {t('demo.preview.contentLabel')}
            </div>
            {lessonBlockTitle ? (
              <p className="mb-1 text-xs font-semibold text-slate-800 dark:text-slate-100">
                <PersianText>{lessonBlockTitle}</PersianText>
              </p>
            ) : null}
            <p className="line-clamp-5 text-sm leading-relaxed text-slate-700 dark:text-slate-100">
              <PersianText>
                {lessonExcerpt || lessonDesc || t('demo.preview.contentPlaceholder')}
              </PersianText>
            </p>
            <div className="mt-3 flex h-8 items-center gap-2 rounded-lg bg-white px-2 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-600">
              <div className="h-5 w-5 rounded-full bg-amber-500" />
              <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-600">
                <div className="h-full w-1/3 rounded-full bg-amber-500" />
              </div>
            </div>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="lesson-practice">
        <GuideSpotlight
          active={activeTarget === 'lesson-practice'}
          onSelect={() => selectTarget('lesson-practice')}
        >
          <div className="rounded-xl border border-teal-200/80 bg-teal-50/70 p-3 dark:border-teal-600/45 dark:bg-teal-900/35">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-teal-900 dark:text-teal-100">
                <Target className="h-3.5 w-3.5" aria-hidden />
                {t('demo.preview.practiceLabel')}
              </div>
              {lesson && lesson.xpScore > 0 ? (
                <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300">
                  {t('demo.xpPoints', { count: lesson.xpScore })}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-[11px] text-teal-800/80 dark:text-teal-200/90">
              {t('demo.preview.practiceHint')}
            </p>
          </div>
        </GuideSpotlight>
      </div>
    </div>
  );

  const renderQuizStage = () => (
    <div className="space-y-3">
      <div data-demo-guide="quiz-header">
        <GuideSpotlight
          active={activeTarget === 'quiz-header'}
          onSelect={() => selectTarget('quiz-header')}
        >
          <div className="rounded-xl border border-teal-200/80 bg-gradient-to-br from-teal-50 to-white p-3 dark:border-teal-600/45 dark:from-teal-900/40 dark:to-slate-800">
            <p className="text-[10px] font-bold uppercase tracking-wider text-teal-800/80 dark:text-teal-200">
              {t('demo.sampleQuiz')}
            </p>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              <PersianText>{practiceTitle}</PersianText>
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              {t('demo.inLesson', { lesson: practiceLesson })}
            </p>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="quiz-timer">
        <GuideSpotlight
          active={activeTarget === 'quiz-timer'}
          onSelect={() => selectTarget('quiz-timer')}
        >
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-700/80">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-100">
              <Clock className="h-3.5 w-3.5 text-teal-600 dark:text-teal-300" aria-hidden />
              {practice?.timeLimitMinutes
                ? t('demo.timeLimit', { minutes: practice.timeLimitMinutes })
                : t('demo.preview.noTimeLimit')}
            </span>
            {practice?.passingScore ? (
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-200">
                {t('demo.passingScore', { score: practice.passingScore })}
              </span>
            ) : null}
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="quiz-question">
        <GuideSpotlight
          active={activeTarget === 'quiz-question'}
          onSelect={() => selectTarget('quiz-question')}
        >
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/70">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('demo.preview.quizQuestionSample')}
            </p>
            <div className="mt-2 space-y-1.5">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    n === 1
                      ? 'border-teal-400 bg-teal-50 text-teal-900 dark:border-teal-500 dark:bg-teal-900/45 dark:text-teal-50'
                      : 'border-slate-200 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {t(`demo.preview.quizOption${n}`)}
                </div>
              ))}
            </div>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="quiz-submit">
        <GuideSpotlight
          active={activeTarget === 'quiz-submit'}
          onSelect={() => selectTarget('quiz-submit')}
        >
          <div className="rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-md">
            {t('demo.preview.quizSubmit')}
            {practice && practice.xpScore > 0 ? (
              <span className="ms-2 opacity-90">{t('demo.xpPoints', { count: practice.xpScore })}</span>
            ) : null}
          </div>
        </GuideSpotlight>
      </div>
    </div>
  );

  const renderExerciseStage = () => (
    <div className="space-y-3">
      <div data-demo-guide="exercise-header">
        <GuideSpotlight
          active={activeTarget === 'exercise-header'}
          onSelect={() => selectTarget('exercise-header')}
        >
          <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white p-3 dark:border-emerald-600/45 dark:from-emerald-900/40 dark:to-slate-800">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-800/80 dark:text-emerald-200">
              {t('demo.sampleExercise')}
            </p>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              <PersianText>{practiceTitle}</PersianText>
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              {t('demo.inLesson', { lesson: practiceLesson })}
            </p>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="exercise-prompt">
        <GuideSpotlight
          active={activeTarget === 'exercise-prompt'}
          onSelect={() => selectTarget('exercise-prompt')}
        >
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/70">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('demo.preview.exercisePromptSample')}
            </p>
            <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-400 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-400">
              {t('demo.preview.exerciseAnswerPlaceholder')}
            </div>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="exercise-check">
        <GuideSpotlight
          active={activeTarget === 'exercise-check'}
          onSelect={() => selectTarget('exercise-check')}
        >
          <div className="rounded-xl bg-emerald-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-md">
            {t('demo.preview.exerciseCheck')}
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="exercise-progress">
        <GuideSpotlight
          active={activeTarget === 'exercise-progress'}
          onSelect={() => selectTarget('exercise-progress')}
        >
          <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-700/80">
            <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-700 dark:text-slate-100">
              <span>{t('demo.preview.exerciseProgress')}</span>
              <span>
                {t('demo.questionCount', { count: practice?.questionCount || 3 })}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-600">
              <div className="h-full w-1/3 rounded-full bg-emerald-500" />
            </div>
          </div>
        </GuideSpotlight>
      </div>
    </div>
  );

  const renderBookStage = () => (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div data-demo-guide="book-cover" className="shrink-0">
          <GuideSpotlight
            active={activeTarget === 'book-cover'}
            onSelect={() => selectTarget('book-cover')}
            className="!w-auto"
          >
            <div className="h-28 w-20 overflow-hidden rounded-lg bg-sky-100 shadow-md dark:bg-sky-900/50">
              {bookCover ? (
                <img src={bookCover} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-sky-700 dark:text-sky-300">
                  <Library className="h-8 w-8" aria-hidden />
                </div>
              )}
            </div>
          </GuideSpotlight>
        </div>
        <div data-demo-guide="book-meta" className="min-w-0 flex-1">
          <GuideSpotlight
            active={activeTarget === 'book-meta'}
            onSelect={() => selectTarget('book-meta')}
            className="h-full"
          >
            <div className="h-full rounded-xl border border-sky-200/80 bg-sky-50/80 p-3 dark:border-sky-600/45 dark:bg-sky-900/35">
              <p className="text-[10px] font-bold uppercase tracking-wider text-sky-800/80 dark:text-sky-200">
                {t('demo.sampleBook')}
              </p>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                <PersianText>{bookTitle}</PersianText>
              </h3>
              {bookAuthor ? (
                <p className="text-xs text-slate-600 dark:text-slate-300">
                  <PersianText>{bookAuthor}</PersianText>
                </p>
              ) : null}
            </div>
          </GuideSpotlight>
        </div>
      </div>

      <div data-demo-guide="book-pages">
        <GuideSpotlight
          active={activeTarget === 'book-pages'}
          onSelect={() => selectTarget('book-pages')}
        >
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700/70">
            <p className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-100">
              {t('demo.preview.bookPagesLabel')}
            </p>
            {bookBlockTitle ? (
              <p className="mb-1 text-xs font-semibold text-slate-800 dark:text-slate-100">
                <PersianText>{bookBlockTitle}</PersianText>
              </p>
            ) : null}
            <p className="line-clamp-5 text-sm leading-relaxed text-slate-700 dark:text-slate-100">
              <PersianText>
                {bookExcerpt || bookDesc || t('demo.preview.bookPagesPlaceholder')}
              </PersianText>
            </p>
            <div className="mt-3 flex h-8 items-center gap-2 rounded-lg bg-white px-2 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-600">
              <div className="h-5 w-5 rounded-full bg-sky-500" />
              <div className="h-1.5 flex-1 rounded-full bg-slate-200 dark:bg-slate-600">
                <div className="h-full w-2/5 rounded-full bg-sky-500" />
              </div>
            </div>
          </div>
        </GuideSpotlight>
      </div>

      <div data-demo-guide="book-review">
        <GuideSpotlight
          active={activeTarget === 'book-review'}
          onSelect={() => selectTarget('book-review')}
        >
          <div className="flex items-center justify-between rounded-xl border border-sky-200 bg-white px-3 py-2.5 dark:border-sky-600/45 dark:bg-slate-700/80">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-800 dark:text-sky-100">
              <CheckCircle className="h-3.5 w-3.5" aria-hidden />
              {t('demo.preview.bookReview')}
            </span>
            <Zap className="h-4 w-4 text-amber-500" aria-hidden />
          </div>
        </GuideSpotlight>
      </div>
    </div>
  );

  const stage =
    kind === 'lesson'
      ? renderLessonStage()
      : kind === 'quiz'
        ? renderQuizStage()
        : kind === 'exercise'
          ? renderExerciseStage()
          : renderBookStage();

  const node = (
    <div
      className={`fixed inset-0 z-[140] flex items-end justify-center bg-black/50 p-3 backdrop-blur-[2px] transition-opacity duration-200 sm:items-center sm:p-6 dark:bg-black/45 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-demo-preview-title"
      onClick={close}
    >
      <div
        className={`flex max-h-[min(92vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-transform duration-300 dark:border-slate-600 dark:bg-slate-800 ${
          visible ? 'translate-y-0 scale-100' : 'translate-y-6 scale-[0.98]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-600 sm:px-5">
          <div className="min-w-0 text-start">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800/80 dark:text-amber-300">
              {t('demo.preview.eyebrow')}
            </p>
            <h2 id="home-demo-preview-title" className="text-lg font-bold text-slate-900 dark:text-white">
              {t(`demo.preview.${kind}.title`)}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
            aria-label={t('demo.preview.closeAria')}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div ref={stageRef} className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {stage}
        </div>

        <div className="border-t border-slate-200 bg-slate-50/90 px-4 py-3 dark:border-slate-600 dark:bg-slate-700/60 sm:px-5">
          <p className="text-start text-sm font-semibold text-slate-900 dark:text-white">
            {t(`demo.preview.guide.${activeTarget}.title`)}
          </p>
          <p className="mt-1 text-start text-xs leading-relaxed text-slate-600 dark:text-slate-200 sm:text-sm">
            {t(`demo.preview.guide.${activeTarget}.body`)}
          </p>
          <div className="mt-3 flex items-center gap-1.5">
            {targets.map((target, i) => (
              <button
                key={target}
                type="button"
                onClick={() => setGuideIndex(i)}
                className={`h-1.5 flex-1 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                  i <= guideIndex ? 'bg-amber-600 dark:bg-amber-400' : 'bg-slate-200 dark:bg-slate-600'
                }`}
                aria-label={t(`demo.preview.guide.${target}.title`)}
                aria-current={i === guideIndex ? 'step' : undefined}
              />
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={goBackGuide}
              disabled={guideIndex === 0}
              className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 disabled:opacity-40 dark:text-slate-200"
            >
              {t('demo.back')}
            </button>
            <button
              type="button"
              onClick={goNextGuide}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-2 text-sm font-semibold text-white shadow-md"
            >
              {isLastGuide ? t('demo.preview.done') : t('demo.next')}
              {!isLastGuide ? (
                <ArrowRight className={localeMirroredNavArrowClass(isRtl, 'h-4 w-4')} aria-hidden />
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default HomeProductDemoPreview;
